import React from 'react';
import PixelButton from '../components/PixelButton';
import PixelBlast from '../components/PixelBlast';
import PixelTransition from '../components/PixelTransition';
import photo1 from '../assets/photo1.jpg';
import photo2 from '../assets/photo2.png';
import photo3 from '../assets/photo3.jpg';
import originalPhoto1 from '../assets/original_photo1.jpg';
import originalPhoto2 from '../assets/original_photo2.png';
import originalPhoto3 from '../assets/original_photo3.jpg';
import { FaCamera, FaSlidersH, FaShareAlt, FaBan } from 'react-icons/fa';

// Main Home Component
const Home = () => {
    return (
        <div className="min-h-screen bg-[#0E101D] overflow-x-hidden">
            {/* Hero - Full Viewport */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
                {/* PixelBlast Background Animation */}
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'auto' }}>
                    <PixelBlast
                        variant="square"
                        pixelSize={4}
                        color="#8c1717"
                        patternScale={2}
                        patternDensity={1}
                        pixelSizeJitter={0}
                        enableRipples
                        rippleSpeed={0.4}
                        rippleThickness={0.12}
                        rippleIntensityScale={1.5}
                        liquid={false}
                        liquidStrength={0.12}
                        liquidRadius={1.2}
                        liquidWobbleSpeed={5}
                        speed={0.5}
                        edgeFade={0.25}
                        transparent
                    />
                </div>
                <div className="relative z-10 container mx-auto px-4 text-center">
                    <h1 className="text-6xl md:text-7xl lg:text-9xl font-vt323 text-[#ff0055] mb-6 uppercase leading-none tracking-widest" style={{ textShadow: '4px 4px 0 #fff' }}>
                        Turn Your<br /><span className="text-white" style={{ textShadow: '4px 4px 0 #ff0055' }}>World Into</span><br />8-Bit Art
                    </h1>
                    <p className="text-xl md:text-2xl font-vt323 text-gray-300 mb-10 max-w-2xl mx-auto">
                        Capture photos with real-time pixelation and retro filters. The ultimate retro aesthetic camera for your phone.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a href="https://apps.apple.com/de/app/pixelated-baran/id6756602667" target="_blank" rel="noopener noreferrer">
                            <PixelButton>Download App</PixelButton>
                        </a>
                        <PixelButton secondary>Learn More</PixelButton>
                    </div>
                </div>


            </section>

            {/* Photo Gallery Section */}
            <section className="py-20 bg-[#0E101D] border-t-4 border-[#1a1c2e]">
                <div className="container mx-auto px-4">
                    <h2 className="text-4xl md:text-5xl font-vt323 text-center text-white mb-12 uppercase tracking-widest">
                        <span className="border-b-4 border-[#00d0ff] pb-2">Pixel Art Gallery</span>
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        <PixelTransition
                            className="hover:border-[#ff0055] transition-colors"
                            aspectRatio="133.33%"
                            gridSize={12}
                            pixelColor="#ff0055"
                            firstContent={
                                <img src={photo1} alt="Pixel Art Tree" className="w-full h-full object-cover" />
                            }
                            secondContent={
                                <img src={originalPhoto3} alt="Original Tree" className="w-full h-full object-cover" />
                            }
                        />
                        <PixelTransition
                            className="hover:border-[#00d0ff] transition-colors md:translate-y-8"
                            aspectRatio="133.33%"
                            gridSize={12}
                            pixelColor="#00d0ff"
                            firstContent={
                                <img src={photo2} alt="Pixel Art Bar" className="w-full h-full object-cover" />
                            }
                            secondContent={
                                <img src={originalPhoto1} alt="Original Bar" className="w-full h-full object-cover" />
                            }
                        />
                        <PixelTransition
                            className="hover:border-[#ffdd00] transition-colors"
                            aspectRatio="133.33%"
                            gridSize={12}
                            pixelColor="#ffdd00"
                            firstContent={
                                <img src={photo3} alt="Pixel Art Sky" className="w-full h-full object-cover" />
                            }
                            secondContent={
                                <img src={originalPhoto2} alt="Original Sky" className="w-full h-full object-cover" />
                            }
                        />
                    </div>
                </div>
            </section>

            {/* Features */}
            <section className="py-20 bg-[#161823] border-t-4 border-[#2d2d2d]">
                <div className="container mx-auto px-4">
                    <h2 className="text-4xl md:text-5xl font-vt323 text-center text-white mb-16 uppercase tracking-widest">
                        <span className="border-b-4 border-[#ff0055] pb-2">Features</span>
                    </h2>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        <FeatureCard
                            icon={<FaCamera />}
                            title="Real-time Filters"
                            desc="See the pixel effect live before you snap."
                            color="text-[#00d0ff]"
                        />
                        <FeatureCard
                            icon={<FaSlidersH />}
                            title="Custom Resolution"
                            desc="Adjust pixel size from chunky blocks to fine details."
                            color="text-[#ffdd00]"
                        />
                        <FeatureCard
                            icon={<FaShareAlt />}
                            title="One-Tap Sharing"
                            desc="Share your masterpieces directly to social media."
                            color="text-[#ff0055]"
                        />
                        <FeatureCard
                            icon={<FaBan />}
                            title="No Ads"
                            desc="Clean experience. No watermarks. Just art."
                            color="text-[#00ff99]"
                        />
                    </div>
                </div>
            </section>
        </div>
    );
};

const FeatureCard = ({ icon, title, desc, color }) => (
    <div className="bg-[#0E101D] p-8 border-4 border-[#2d2d2d] hover:border-[#ff0055] transition-colors group">
        <div className={`text-4xl mb-4 ${color} group-hover:scale-110 transition-transform duration-300`}>
            {icon}
        </div>
        <h3 className="text-2xl font-vt323 text-white mb-2 uppercase">{title}</h3>
        <p className="font-vt323 text-gray-400 text-lg leading-tight">{desc}</p>
    </div>
);

export default Home;
